/**
 * Carga de tecnologías de medición atada al consentimiento.
 *
 * Los scripts de GTM, GA4, Clarity y Meta Pixel ya no viven en `index.html`:
 * se inyectan desde acá solo cuando la persona acepta su categoría, y se
 * revocan (cookies de medición/atribución + señal de desactivación) cuando la
 * retira. Sin consentimiento no hay red hacia esos terceros.
 */

import { onConsentChange, readConsent } from "./consent";
import type { ConsentState } from "./consent";

type TrackingWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  fbq?: (...args: unknown[]) => void;
  _fbq?: unknown;
  clarity?: (...args: unknown[]) => void;
};

const loaded = { gtm: false, ga4: false, clarity: false, pixel: false };

const appendScript = (src: string) => {
  if (typeof document === "undefined") return;
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const loadGtm = () => {
  const id = import.meta.env.VITE_GTM_ID;
  if (!id || loaded.gtm) return;
  loaded.gtm = true;

  const w = window as TrackingWindow;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  appendScript(`https://www.googletagmanager.com/gtm.js?id=${id}`);
};

const loadGa4 = () => {
  const id = import.meta.env.VITE_GA4_ID;
  if (!id || loaded.ga4) return;
  loaded.ga4 = true;

  const w = window as TrackingWindow;
  w.dataLayer = w.dataLayer || [];
  w.gtag = (...args: unknown[]) => {
    w.dataLayer?.push(args);
  };
  w.gtag("js", new Date());
  w.gtag("config", id, { send_page_view: false });
  appendScript(`https://www.googletagmanager.com/gtag/js?id=${id}`);
};

const loadClarity = () => {
  const id = import.meta.env.VITE_CLARITY_ID;
  if (!id || loaded.clarity) return;
  loaded.clarity = true;

  const w = window as TrackingWindow;
  type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[] };
  const clarity = ((...args: unknown[]) => {
    clarity.q = clarity.q ?? [];
    clarity.q.push(args);
  }) as ClarityFn;
  clarity.q = [];
  w.clarity = clarity;
  appendScript(`https://www.clarity.ms/tag/${id}`);
};

const loadPixel = () => {
  const id = import.meta.env.VITE_META_PIXEL_ID;
  if (!id || loaded.pixel) return;
  loaded.pixel = true;

  const w = window as TrackingWindow;
  if (!w.fbq) {
    type Fbq = ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[];
      loaded?: boolean;
      version?: string;
      push?: (...args: unknown[]) => void;
    };
    const fbq = ((...args: unknown[]) => {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue?.push(args);
    }) as Fbq;
    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.push = fbq;
    w.fbq = fbq;
    w._fbq = fbq;
  }

  appendScript("https://connect.facebook.net/en_US/fbevents.js");
  w.fbq?.("init", id);
};

const deleteCookie = (name: string) => {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
};

const revokeAnalytics = () => {
  const id = import.meta.env.VITE_GA4_ID;
  if (id) {
    // GA4 lee esta bandera global para dejar de enviar hits.
    const globalFlags = window as unknown as Record<string, unknown>;
    globalFlags[`ga-disable-${id}`] = true;
    deleteCookie(`_ga_${id.replace(/^G-/, "")}`);
  }
  ["_ga", "_gid", "_clck", "_clsk"].forEach(deleteCookie);
};

const revokeMarketing = () => {
  const w = window as TrackingWindow;
  w.fbq?.("consent", "revoke");
  ["_fbp", "_fbc"].forEach(deleteCookie);
};

export const applyConsent = (
  consent: ConsentState,
  options: { pageView?: boolean } = {},
) => {
  if (typeof window === "undefined") return;

  if (consent.analytics) {
    loadGtm();
    loadGa4();
    loadClarity();
  } else {
    revokeAnalytics();
  }

  if (consent.marketing) {
    loadPixel();
  } else {
    revokeMarketing();
  }

  if (!options.pageView) return;

  const w = window as TrackingWindow;
  if (consent.analytics && typeof w.gtag === "function") {
    w.gtag("event", "page_view", {
      page_path: window.location.pathname + window.location.search,
      page_location: window.location.href,
    });
  }
  if (consent.marketing && typeof w.fbq === "function") {
    w.fbq("track", "PageView");
  }
};

/** Aplica la decisión guardada y reacciona a cambios desde el banner. */
export const initTracking = () => {
  if (typeof window === "undefined") return;

  const stored = readConsent();
  if (stored) applyConsent(stored, { pageView: false });

  onConsentChange((next) => {
    if (next) {
      applyConsent(next, { pageView: true });
      return;
    }
    revokeAnalytics();
    revokeMarketing();
  });
};
